import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import {
  Printer, Download, CheckCircle, Loader2, AlertTriangle, ChevronRight,
  Image as ImageIcon, FileCheck, ExternalLink, Edit3, MessageSquare,
} from 'lucide-react';
import ChapterEditor from './ChapterEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// review.target: 'cover' (book detail / cover preview) | 'pdf' (full PDF)
// Reduced from 4 to 1 item on 2026-05-16 per user feedback — only the
// visual review of the 3 cover panels is meaningful at the app level.
const CHECKLIST_ITEMS = [
  {
    key: 'spine_photo_ok', icon: ImageIcon, label: 'Portada completa (3 paneles)',
    help: 'Contraportada, lomo y portada bien encuadrados, sin recortes, textos legibles y al tamaño real del libro.',
    review: { target: 'cover', label: 'Ver portada' },
  },
];

/**
 * Admin-only dialog: lists books currently in ``preparacion_imprenta`` and
 * lets the admin (a) review each one, (b) check off the technical
 * pre-print checklist, (c) approve the book to ``envio_imprenta``.
 *
 * Flow:
 *   1. Top-level grid shows all pending books with metadata + a "Revisar"
 *      button per row.
 *   2. Clicking "Revisar" opens an inline detail view with the PDF
 *      download button and the 4-item checklist.
 *   3. When all 4 items are checked, the "Aprobar → enviar a imprenta"
 *      button enables. Clicking it transitions the book.
 */
export const PrintPreparationDialog = ({ open, onClose, onApproved }) => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBookId, setSelectedBookId] = useState(null);
  const [checklistState, setChecklistState] = useState({ items: {}, notes: '' });
  const [savingChecklist, setSavingChecklist] = useState(false);
  const [approving, setApproving] = useState(false);
  // Chapter editor in "propose" mode (admin reviewer) — opens on demand.
  const [chapterEditorOpen, setChapterEditorOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/books-pending-print-prep`, { withCredentials: true });
      setBooks(res.data.books || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudieron cargar los libros');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      refresh();
      setSelectedBookId(null);
    }
  }, [open, refresh]);

  const selectedBook = books.find((b) => b.book_id === selectedBookId);

  useEffect(() => {
    if (selectedBook) {
      setChecklistState({
        items: { ...selectedBook.checklist_items },
        notes: selectedBook.checklist_notes || '',
      });
    }
  }, [selectedBook?.book_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateChecklist = (key, value) => {
    setChecklistState((prev) => ({
      ...prev,
      items: { ...prev.items, [key]: value },
    }));
  };

  const saveChecklist = async (autoSilent = false) => {
    if (!selectedBookId) return;
    setSavingChecklist(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/books/${selectedBookId}/print-checklist`,
        { items: checklistState.items, notes: checklistState.notes },
        { withCredentials: true },
      );
      if (!autoSilent) toast.success('Checklist guardado');
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSavingChecklist(false);
    }
  };

  const approveForPrint = async () => {
    if (!selectedBookId) return;
    setApproving(true);
    try {
      // Save first, then approve.
      await axios.put(
        `${API_URL}/api/admin/books/${selectedBookId}/print-checklist`,
        { items: checklistState.items, notes: checklistState.notes },
        { withCredentials: true },
      );
      const res = await axios.post(
        `${API_URL}/api/admin/books/${selectedBookId}/approve-for-print`,
        {}, { withCredentials: true },
      );
      toast.success(`🖨️ Libro aprobado. Pasa a ${res.data.new_status === 'envio_imprenta' ? 'envío imprenta' : res.data.new_status}.`, { duration: 5000 });
      onApproved?.(selectedBookId);
      setSelectedBookId(null);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo aprobar');
    } finally {
      setApproving(false);
    }
  };

  const completedCount = Object.values(checklistState.items || {}).filter(Boolean).length;
  const allCheckedLocal = completedCount === CHECKLIST_ITEMS.length;

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }) +
        ' · ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return iso;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="print-preparation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="w-5 h-5 text-teal-600" />
            Preparación para imprenta
            <span className="text-sm font-normal text-muted-foreground">
              ({books.length} {books.length === 1 ? 'libro pendiente' : 'libros pendientes'})
            </span>
          </DialogTitle>
          <DialogDescription>
            Revisa cada libro y completa el checklist técnico antes de enviar a imprenta.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : books.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground">
            <Printer className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No hay libros esperando preparación para imprenta.</p>
          </div>
        ) : !selectedBook ? (
          /* List view — all pending books */
          <div className="space-y-2 mt-2">
            {books.map((b) => {
              const protag = `${b.profile?.recipient_name || ''} ${b.profile?.recipient_last_name || ''}`.trim();
              return (
                <Card
                  key={b.book_id}
                  className={`hover:bg-teal-50/30 cursor-pointer transition-colors border-l-4 ${b.all_checks_pass ? 'border-l-emerald-500' : 'border-l-teal-300'}`}
                  onClick={() => setSelectedBookId(b.book_id)}
                  data-testid={`pending-book-${b.book_id}`}
                >
                  <CardContent className="p-4 flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{b.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Para <strong>{protag || '(sin nombre)'}</strong>
                        {' · '}
                        Promotor: <strong>{b.user_name || b.user_email}</strong>
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        Confirmado el {formatDate(b.cover_confirmed_at)} · {b.page_count || '?'} páginas
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {b.pending_corrections > 0 && (
                        <span
                          className="text-xs px-2 py-1 rounded-full font-medium bg-amber-100 text-amber-800 flex items-center gap-1"
                          title="Correcciones pendientes de aprobación del promotor"
                          data-testid={`pending-corrections-badge-${b.book_id}`}
                        >
                          <MessageSquare className="w-3 h-3" />
                          {b.pending_corrections} {b.pending_corrections === 1 ? 'corrección' : 'correcciones'}
                        </span>
                      )}
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${b.all_checks_pass && (b.pending_corrections || 0) === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-teal-100 text-teal-700'}`}>
                        {b.checklist_completed}/{b.checklist_total} checks
                      </span>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          /* Detail view — selected book */
          <div className="space-y-4 mt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedBookId(null)}
              className="gap-1 text-xs h-7 -ml-2"
              data-testid="back-to-list-btn"
            >
              ← Volver al listado
            </Button>

            <div className="rounded-lg border p-3 bg-muted/30">
              <p className="font-semibold text-sm">{selectedBook.title}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Para <strong>{`${selectedBook.profile?.recipient_name || ''} ${selectedBook.profile?.recipient_last_name || ''}`.trim()}</strong>
                {' · '}
                Promotor: <strong>{selectedBook.user_name || selectedBook.user_email}</strong>
                {' · '}
                Confirmado el {formatDate(selectedBook.cover_confirmed_at)}
              </p>
              <p className="text-xs text-muted-foreground">
                {selectedBook.page_count || '?'} páginas · plantilla: {selectedBook.cover_template_id || '—'}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2 gap-2 text-xs"
                onClick={() => window.open(`${API_URL}/api/books/${selectedBook.book_id}/full-pdf`, '_blank')}
                data-testid="download-pdf-btn"
              >
                <Download className="w-3 h-3" /> Descargar PDF para revisar
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="mt-2 ml-2 gap-2 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                onClick={() => setChapterEditorOpen(true)}
                data-testid="edit-content-btn"
              >
                <Edit3 className="w-3 h-3" /> Editar contenido (proponer correcciones)
              </Button>
            </div>

            {selectedBook.pending_corrections > 0 && (
              <div
                className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-300"
                data-testid="pending-corrections-warning"
              >
                <MessageSquare className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-900">
                    {selectedBook.pending_corrections} {selectedBook.pending_corrections === 1 ? 'corrección pendiente' : 'correcciones pendientes'} de aprobación del promotor
                  </p>
                  <p className="text-xs text-amber-800 mt-0.5">
                    No se puede aprobar el libro para imprenta hasta que el promotor apruebe o rechace todas las propuestas.
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <p className="text-sm font-semibold flex items-center gap-2">
                <FileCheck className="w-4 h-4 text-teal-600" />
                Revisión visual
                <span className="text-xs font-normal text-muted-foreground">
                  ({completedCount}/{CHECKLIST_ITEMS.length} completado)
                </span>
              </p>
              {CHECKLIST_ITEMS.map((item) => {
                const Icon = item.icon;
                const checked = !!checklistState.items[item.key];

                const handleReview = (e) => {
                  // Stop the click bubbling to the parent <label> (which would
                  // toggle the checkbox).
                  e.preventDefault();
                  e.stopPropagation();
                  if (!selectedBook) return;
                  if (item.review.target === 'cover') {
                    window.open(`/books/${selectedBook.book_id}`, '_blank', 'noopener,noreferrer');
                  } else if (item.review.target === 'pdf') {
                    window.open(`${API_URL}/api/books/${selectedBook.book_id}/full-pdf`, '_blank', 'noopener,noreferrer');
                  }
                };

                return (
                  <label
                    key={item.key}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checked ? 'border-emerald-300 bg-emerald-50/40' : 'border-gray-200 hover:bg-muted/40'}`}
                    data-testid={`check-${item.key}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) => updateChecklist(item.key, !!v)}
                      data-testid={`check-${item.key}-input`}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium flex items-center gap-1.5">
                        <Icon className="w-3.5 h-3.5 text-teal-600" />
                        {item.label}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">{item.help}</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleReview}
                      className="flex-shrink-0 gap-1 text-xs h-7"
                      data-testid={`review-${item.key}-btn`}
                    >
                      <ExternalLink className="w-3 h-3" />
                      {item.review.label}
                    </Button>
                  </label>
                );
              })}
            </div>

            <div>
              <p className="text-sm font-medium mb-1.5">Notas internas (opcional)</p>
              <Textarea
                value={checklistState.notes}
                onChange={(e) => setChecklistState((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Notas para imprenta o el equipo (papel sugerido, tirada, observaciones…)"
                className="min-h-[70px] text-sm resize-none"
                data-testid="checklist-notes-input"
              />
            </div>

            {!allCheckedLocal && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-900">
                  Revisa la portada (3 paneles) y marca el check antes de aprobar el libro para imprenta.
                </p>
              </div>
            )}
          </div>
        )}

        {selectedBook && (
          <DialogFooter className="gap-2 mt-2">
            <Button
              variant="outline"
              onClick={() => saveChecklist(false)}
              disabled={savingChecklist || approving}
              data-testid="save-checklist-btn"
            >
              {savingChecklist ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Guardar checklist
            </Button>
            <Button
              onClick={approveForPrint}
              disabled={approving || savingChecklist || !allCheckedLocal || (selectedBook.pending_corrections || 0) > 0}
              className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="approve-print-btn"
              title={(selectedBook.pending_corrections || 0) > 0 ? 'Hay correcciones pendientes de aprobación del promotor' : ''}
            >
              {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              Aprobar → enviar a imprenta
            </Button>
          </DialogFooter>
        )}
      </DialogContent>

      {/* Chapter editor opens in "propose" mode — admin proposes
          corrections that the promoter must approve. */}
      {selectedBook && (
        <ChapterEditor
          open={chapterEditorOpen}
          onOpenChange={setChapterEditorOpen}
          bookId={selectedBook.book_id}
          mode="propose"
          onSaved={async () => {
            // Refresh the book list so the pending-corrections count
            // updates immediately in both the list and detail view.
            await refresh();
          }}
        />
      )}
    </Dialog>
  );
};

export default PrintPreparationDialog;
